import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
// Import Components
import TemplateInfoModal from 'components/TemplateModal/TemplateInfoModal';
import SegmentModal from 'components/SegmentModal/SegmentModal';
import CustomerJourneyActionsModals from 'components/CustomerJourneyList/CustomerJourneyActionsModals';
import StateFullCampaignActionsModals from './StateFullCampaignActionsModals';
import ReportActionsModals from 'components/ReportList/ReportActionsModals';
// Import Hooks
import useToaster from 'hooks/UseToaster';
// Import Constants
import { DefinitionTypes, ModalsActions } from 'constants/common';
import { modalsStateSetDictKeys, globalSearchObjectKeys } from './config';

const GlobalSearchActionsModals = (props) => {
    const {
        openedModalData,
        isSegmentInfoModalOpen,
        isTemplateInfoModalOpen,
        isCampaignInfoModalOpen,
        isRealtimePromotionInfoModalOpen,
        isReportInfoModalOpen,
        onCloseModalHandler,
        infoModalsStateSetDict,
    } = props;
    const { segment, template, campaign } = openedModalData;
    const { showToaster } = useToaster();

    const [globalOpenedModalData, setGlobalOpenedModalData] = useState(openedModalData);

    useEffect(() => {
        if (!isNil(openedModalData)) {
            setGlobalOpenedModalData(openedModalData);
        }
    }, [openedModalData]);

    // Customer Journey Action Modals States
    const [isCustomerJourneyDeleteModalOpenedState, setIsCustomerJourneyDeleteModalOpenedState] = useState(false);
    const [
        isCustomerJourneyUsedInConfirmationModalOpenedState,
        setIsCustomerJourneyUsedInConfirmationModalOpenedState,
    ] = useState(false);
    const [isCustomerJourneyArchiveModalOpenedState, setIsCustomerJourneyArchiveModalOpenedState] = useState(false);
    const [isCustomerJourneyUnArchiveModalOpenedState, setIsCustomerJourneyUnArchiveModalOpenedState] = useState(false);
    const [isCustomerJourneyStopSendModalOpenedState, setIsCustomerJourneyStopSendModalOpenedState] = useState(false);
    const [isCustomerJourneyStartModalOpenedState, setIsCustomerJourneyStartModalOpenedState] = useState(false);
    const [isCustomerJourneyResumeModalOpenedState, setIsCustomerJourneyResumeModalOpenedState] = useState(false);

    // Report Actions Modals States
    const [isReportDeleteModalOpenedState, setIsReportDeleteModalOpenedState] = useState(false);
    const [isReportStopScheduleModalOpenedState, setIsReportStopScheduleModalOpenedState] = useState(false);
    const [isReportExecuteModalOpenedState, setIsReportExecuteModalOpenedState] = useState(false);
    const [isReportUsedInConfirmationModalOpenedState, setIsReportUsedInConfirmationModalOpenedState] = useState(false);
    const [isReportArchiveModalOpenedState, setIsReportArchiveModalOpenedState] = useState(false);
    const [isReportUnarchiveModalOpenedState, setIsReportUnarchiveModalOpenedState] = useState(false);

    const modalsStateSetDict = {
        // CJ actions
        [modalsStateSetDictKeys.deleteCustomerJourney]: {
            key: 'deleteCustomerJourney',
            fn: setIsCustomerJourneyDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.editCustomerJourney]: {
            key: 'editCustomerJourney',
            fn: setIsCustomerJourneyUsedInConfirmationModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveCustomerJourney]: {
            key: 'archiveCustomerJourney',
            fn: setIsCustomerJourneyArchiveModalOpenedState,
        },
        unarchiveCustomerJourney: {
            key: 'unarchiveCustomerJourney',
            fn: setIsCustomerJourneyUnArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.stopSendCustomerJourney]: {
            key: 'stopSendCustomerJourney',
            fn: setIsCustomerJourneyStopSendModalOpenedState,
        },
        [modalsStateSetDictKeys.startCustomerJourney]: {
            key: 'startCustomerJourney',
            fn: setIsCustomerJourneyStartModalOpenedState,
        },
        [modalsStateSetDictKeys.resumeCustomerJourney]: {
            key: 'resumeCustomerJourney',
            fn: setIsCustomerJourneyResumeModalOpenedState,
        },
        [modalsStateSetDictKeys.infoCustomerJourney]: { key: 'infoCustomerJourney' },

        // Report Actions
        [modalsStateSetDictKeys.deleteReport]: {
            key: 'deleteReport',
            fn: setIsReportDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.stopScheduleReport]: {
            key: 'stopScheduleReport',
            fn: setIsReportStopScheduleModalOpenedState,
        },
        [modalsStateSetDictKeys.infoReport]: {
            key: 'infoReport',
        },
        [modalsStateSetDictKeys.executeReport]: {
            key: 'executeReport',
            fn: setIsReportExecuteModalOpenedState,
        },
        [modalsStateSetDictKeys.editReport]: {
            key: 'editReport',
            fn: setIsReportUsedInConfirmationModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveReport]: {
            key: 'archiveReport',
            fn: setIsReportArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.unarchiveReport]: {
            key: 'unarchiveReport',
            fn: setIsReportUnarchiveModalOpenedState,
        },
    };

    const handleInfoModalActionClick = (stateKey, infoModalKey) => {
        return (modalKey, data) => {
            onCloseModalHandler(infoModalKey);
            if (!isNil(modalKey) && !isNil(data)) {
                setGlobalOpenedModalData((prev) => ({ ...prev, [stateKey]: data }));
                modalsStateSetDict[modalKey].fn?.(true);
            }
        };
    };

    const intermediaryOnCloseModalHandler = (stateKey) => {
        return (modalKey, action, alertType, alertMessage) => {
            if (modalKey in infoModalsStateSetDict) {
                onCloseModalHandler(modalKey);
                setGlobalOpenedModalData((prev) => ({ ...prev, [stateKey]: {} }));
            } else {
                if (
                    action === ModalsActions.DELETE ||
                    action === ModalsActions.ARCHIVE ||
                    action === ModalsActions.UNARCHIVE ||
                    action === ModalsActions.CUSTOMER_JOURNEY_STOP ||
                    action === ModalsActions.START ||
                    action === ModalsActions.RESUME ||
                    action === ModalsActions.UNSCHEDULE_REPORT ||
                    action === ModalsActions.DELETE ||
                    action === ModalsActions.EXECUTE_REPORT ||
                    action === ModalsActions.ARCHIVE ||
                    (action === ModalsActions.UNARCHIVE && modalKey !== modalsStateSetDict.infoReport.key) // TODO why ?
                ) {
                    showToaster(alertType, alertMessage);
                }
                modalsStateSetDict[modalKey].fn?.(false);
            }
        };
    };

    return (
        <>
            {isSegmentInfoModalOpen && (
                <SegmentModal
                    targetObject={segment.targetObject}
                    segmentId={segment.id}
                    segmentType={segment.type}
                    handleClose={() => onCloseModalHandler(infoModalsStateSetDict.infoSegment.key)}
                    isVisible={isSegmentInfoModalOpen}
                    isPredefined={segment.definitionType === DefinitionTypes.predefined}
                />
            )}
            {isTemplateInfoModalOpen && (
                <TemplateInfoModal
                    isPredefined={false}
                    templateId={template.id}
                    handleClose={() => onCloseModalHandler(infoModalsStateSetDict.infoTemplate.key)}
                    isVisible={isTemplateInfoModalOpen}
                    isGlobal={template.isGlobal}
                />
            )}
            {isCampaignInfoModalOpen && (
                <StateFullCampaignActionsModals
                    campaignId={campaign.id}
                    handleClose={() => onCloseModalHandler(infoModalsStateSetDict.infoCampaign.key)}
                />
            )}
            <CustomerJourneyActionsModals
                isDeleteModalOpenedState={isCustomerJourneyDeleteModalOpenedState}
                isUsedInConfirmationModalOpenedState={isCustomerJourneyUsedInConfirmationModalOpenedState}
                isArchiveModalOpenedState={isCustomerJourneyArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isCustomerJourneyUnArchiveModalOpenedState}
                isStopSendModalOpenedState={isCustomerJourneyStopSendModalOpenedState}
                isStartCustomerJourneyModalOpenedState={isCustomerJourneyStartModalOpenedState}
                onCloseModalHandler={intermediaryOnCloseModalHandler(globalSearchObjectKeys.realtimePromotion)}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={globalOpenedModalData?.realtimePromotion}
                isResumeModalOpenedState={isCustomerJourneyResumeModalOpenedState}
                isInfoModalOpenedState={isRealtimePromotionInfoModalOpen}
                handleInfoModalActionClick={handleInfoModalActionClick(
                    globalSearchObjectKeys.realtimePromotion,
                    modalsStateSetDictKeys.infoCustomerJourney,
                )}
            />
            <ReportActionsModals
                isDeleteModalOpenedState={isReportDeleteModalOpenedState}
                isStopScheduleModalOpenedState={isReportStopScheduleModalOpenedState}
                isInfoModalOpenedState={isReportInfoModalOpen}
                isExecuteModalOpenedState={isReportExecuteModalOpenedState}
                isUsedInConfirmationModalOpenedState={isReportUsedInConfirmationModalOpenedState}
                isArchiveModalOpenedState={isReportArchiveModalOpenedState}
                isUnarchiveModalOpenedState={isReportUnarchiveModalOpenedState}
                onCloseModalHandler={intermediaryOnCloseModalHandler(globalSearchObjectKeys.report)}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={globalOpenedModalData?.report ?? {}}
                handleInfoModalActionClick={handleInfoModalActionClick(
                    globalSearchObjectKeys.report,
                    modalsStateSetDictKeys.infoReport,
                )}
            />
        </>
    );
};

GlobalSearchActionsModals.propTypes = {
    isSegmentInfoModalOpen: PropTypes.bool.isRequired,
    isTemplateInfoModalOpen: PropTypes.bool.isRequired,
    isCampaignInfoModalOpen: PropTypes.bool.isRequired,
    isRealtimePromotionInfoModalOpen: PropTypes.bool.isRequired,
    isReportInfoModalOpen: PropTypes.bool.isRequired,
    openedModalData: PropTypes.object.isRequired,
    onCloseModalHandler: PropTypes.func.isRequired,
    infoModalsStateSetDict: PropTypes.object.isRequired,
};

export default memo(GlobalSearchActionsModals);
